import { useEffect, useState } from 'react';
import { Camera, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { nexaApi } from './api';
import { DressingRoom } from './DressingRoom';

const KIND_STYLE = {
  like: 'border-sky-400/30 text-sky-300 bg-sky-400/10',
  dislike: 'border-red-400/30 text-red-300 bg-red-400/10',
  rule: 'border-white/15 text-white/70 bg-white/[0.05]',
};

const LearnedTaste = () => {
  const [prefs, setPrefs] = useState([]);

  useEffect(() => {
    nexaApi.get('/style-prefs').then(({ data }) => setPrefs(data.prefs)).catch(() => {});
  }, []);

  const remove = async (p) => {
    setPrefs((prev) => prev.filter((x) => x.id !== p.id));
    try {
      await nexaApi.delete(`/style-prefs/${p.id}`);
      toast.success('Preference forgotten');
    } catch {
      toast.error('Could not delete');
    }
  };

  return (
    <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-5" data-testid="nexa-learned-taste">
      <div className="flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-sky-400" strokeWidth={1.8} />
        <span className="text-[11px] font-mono tracking-[0.25em] uppercase text-sky-400/70">Learned taste</span>
      </div>
      {prefs.length === 0 ? (
        <p className="text-[13px] text-white/40 mt-3 leading-relaxed">
          Nothing yet — tell NEXA in chat what you like and hate wearing, and it lands here.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2 mt-3">
          {prefs.map((p, i) => (
            <span key={p.id} data-testid={`nexa-taste-chip-${i}`}
              className={`inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-full border text-[12px] ${KIND_STYLE[p.kind] || KIND_STYLE.rule}`}>
              {p.content}
              <button type="button" onClick={() => remove(p)} aria-label="Forget"
                data-testid={`nexa-taste-remove-${i}`}
                className="w-5 h-5 rounded-full flex items-center justify-center opacity-50 hover:opacity-100">
                <X className="w-3 h-3" strokeWidth={2.5} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export const StylingDetail = ({ personality }) => {
  const [data, setData] = useState(null);

  const load = () => {
    nexaApi.get('/wardrobe').then(({ data: d }) => setData(d)).catch(() => setData({ items: [], outfits: [] }));
  };
  useEffect(load, []);

  useEffect(() => {
    if (!data || !data.items.some((i) => !i.thumb_photo_id)) return undefined;
    const t = setInterval(() => {
      nexaApi.get('/wardrobe').then(({ data: d }) => setData(d)).catch(() => {});
    }, 12000);
    return () => clearInterval(t);
  }, [data]);

  if (!data) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="w-5 h-5 border-2 border-sky-400/30 border-t-sky-400 rounded-full animate-spin" />
      </div>
    );
  }

  const direction = (personality?.insights || []).find((i) => i.id === 'image');

  return (
    <div className="flex flex-col gap-8" data-testid="nexa-styling-detail">
      <div className="rounded-3xl border border-sky-400/20 bg-[#0A0A0A] p-6">
        <p className="text-[11px] font-mono tracking-[0.25em] uppercase text-sky-400/80">
          Style direction
        </p>
        <p className="nexa-heading text-2xl font-light tracking-tight mt-2">Quiet luxury · Calm power</p>
        <p className="text-[13px] text-white/55 mt-2 leading-relaxed">
          {direction?.content ||
            'Understated luxury: clean, structured, intentional, mature — expensive without showing off.'}
        </p>
      </div>

      <LearnedTaste />

      <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] px-5 py-4 flex items-center gap-3">
        <Camera className="w-4 h-4 text-sky-400 shrink-0" strokeWidth={1.8} />
        <p className="text-[13px] text-white/55">
          Send NEXA a photo of your outfit in chat — she rates it, gives tips and saves the pieces here.
        </p>
      </div>

      <DressingRoom items={data.items} onReload={load} />
    </div>
  );
};
